/**
 * TurnoAlertBeeper (R55.5) — Componente background che:
 * 1. Polla ogni 60s `/api/task-alerts/pending` per l'org corrente
 * 2. Se ci sono alert non-dismissed → mostra banner rosso + suona beep tipo sveglia
 *    con Web Audio API (2 note alternate 800/1000Hz per la durata configurata dall'admin)
 * 3. Utente tappa "Ho visto" → dismiss via API + stop beep
 *
 * Il beep si attiva solo se l'org ha `alert_sound_enabled=true`.
 * Durata configurabile (10/20/30/60 s) da Impostazioni.
 *
 * Nota browser: il primo beep richiede un'interazione utente per attivare
 * l'AudioContext (autoplay policy). Se il polling scopre un alert prima
 * di qualsiasi tap, il banner viene mostrato senza audio (comunque visibile).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, BellOff, Check } from "lucide-react";

const POLL_INTERVAL_MS = 60_000;
// R60.7 + R60.8: rotte checkout/onboarding/assistenza dove il beeper NON deve apparire
// (perché è a schermo intero e coprirebbe le CTA principali).
const HIDDEN_ON_ROUTES = ["/scegli-piano", "/pagamento-ok", "/pagamento-annullato", "/attiva-piano", "/richiedi-assistenza"];

export default function TurnoAlertBeeper() {
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [sound, setSound] = useState({ enabled: false, duration_sec: 20 });
  const [dismissing, setDismissing] = useState(false);
  const audioCtxRef = useRef(null);
  const stopRef = useRef(null);
  const shownIdsRef = useRef(new Set()); // per evitare re-beep sugli stessi alert

  // Play alarm-clock pattern (2 alternating tones) via Web Audio API for `durationSec`.
  const playAlarm = useCallback((durationSec) => {
    try {
      // Lazy init AudioContext su interazione (workaround autoplay)
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      const startTime = ctx.currentTime;
      const endTime = startTime + durationSec;
      // Pattern: 800Hz per 300ms, silenzio 100ms, 1000Hz per 300ms, silenzio 300ms → loop
      const beepDur = 0.3;
      const gapShort = 0.1;
      const gapLong = 0.3;
      const cycleDur = beepDur + gapShort + beepDur + gapLong;
      const oscillators = [];
      for (let t = startTime; t < endTime; t += cycleDur) {
        oscillators.push(scheduleBeep(ctx, 800, t, beepDur));
        oscillators.push(scheduleBeep(ctx, 1000, t + beepDur + gapShort, beepDur));
      }
      return () => {
        // Stop tutti gli oscillatori pendenti + chiudi il context
        try {
          oscillators.forEach((o) => { try { o.stop(0); } catch { /* noop */ } });
        } catch { /* noop */ }
      };
    } catch (e) {
      console.warn("[beeper] Web Audio non disponibile:", e);
      return null;
    }
  }, []);

  const stopAlarm = useCallback(() => {
    // Ferma tutti gli oscillatori pendenti
    if (stopRef.current) {
      try { stopRef.current(); } catch { /* noop */ }
      stopRef.current = null;
    }
    // Chiude anche l'AudioContext: interrompe qualsiasi suono residuo in coda
    // (osc.stop(0) può non essere affidabile su iOS/Android per oscillatori già in coda)
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/task-alerts/pending");
      const list = data?.alerts || [];
      const s = data?.sound || { enabled: false, duration_sec: 20 };
      setSound(s);
      setAlerts(list);
      // Rileva alert nuovi (id non ancora visti in questa sessione) → suona
      const newIds = list.map((a) => a.id).filter((id) => !shownIdsRef.current.has(id));
      if (newIds.length > 0 && s.enabled) {
        newIds.forEach((id) => shownIdsRef.current.add(id));
        stopAlarm();
        stopRef.current = playAlarm(s.duration_sec) || null;
      }
    } catch (e) {
      // Silent — polling errors non devono disturbare l'UX
      console.warn("[beeper] fetch alerts fallito:", e?.message);
    }
  }, [playAlarm, stopAlarm]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      stopAlarm();
    };
  }, [fetchAlerts, stopAlarm]);

  const dismissAll = async () => {
    setDismissing(true);
    try {
      const ids = alerts.map((a) => a.id);
      await Promise.all(ids.map((id) =>
        api.post(`/task-alerts/${id}/dismiss`).catch(() => {})
      ));
      stopAlarm();
      setAlerts([]);
      toast.success("Allarme silenziato · buon proseguimento");
    } catch {
      toast.error("Errore silenzia allarme");
    } finally {
      setDismissing(false);
    }
  };

  if (alerts.length === 0) return null;
  // R60.7: nascondi su route di checkout/onboarding (banner copriva le CTA)
  if (HIDDEN_ON_ROUTES.some((r) => location.pathname.startsWith(r))) return null;

  // Banner fixed bottom, alto z-index, non blocca la nav
  return createPortal(
    <div
      className="fixed inset-x-0 bottom-16 sm:bottom-6 z-[70] px-3 pointer-events-none"
      data-testid="turno-alert-banner"
    >
      <div className="max-w-md mx-auto pointer-events-auto animate-in fade-in slide-in-from-bottom-4">
        <div className="rounded-2xl border-2 border-red-600 bg-white shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2.5 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">Allarme turno · {alerts.length} incompleto{alerts.length !== 1 ? "i" : ""}</p>
              <p className="text-[11px] text-red-100">Controllo mancante — completa entro fine turno per evitare NC</p>
            </div>
          </div>
          <div className="px-4 py-3 max-h-[35vh] overflow-y-auto">
            {alerts.slice(0, 3).map((a) => (
              <div key={a.id} className="border-b border-slate-100 last:border-0 py-2" data-testid={`turno-alert-item-${a.id}`}>
                <p className="text-[13px] font-semibold text-slate-900">
                  {a.slot_label} · {a.missing_count} task
                </p>
                {a.missing_labels?.length > 0 && (
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    {a.missing_labels.slice(0, 3).join(", ")}
                    {a.missing_labels.length > 3 && "…"}
                  </p>
                )}
              </div>
            ))}
            {alerts.length > 3 && (
              <p className="text-[11px] text-slate-500 italic mt-2">
                +{alerts.length - 3} altri
              </p>
            )}
          </div>
          <div className="px-3 py-2.5 border-t border-slate-200 bg-slate-50 flex gap-2">
            <button
              onClick={dismissAll}
              disabled={dismissing}
              className="flex-1 h-10 rounded-lg bg-slate-900 text-white text-[13px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="turno-alert-dismiss"
            >
              <Check className="w-4 h-4" /> Ho visto · silenzia
            </button>
            {sound.enabled && stopRef.current && (
              <button
                onClick={() => { stopAlarm(); }}
                className="h-10 px-3 rounded-lg bg-slate-200 text-slate-700 text-[13px] font-semibold flex items-center gap-1.5"
                data-testid="turno-alert-mute"
                title="Silenzia solo il suono (mantieni banner)"
              >
                <BellOff className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Schedula un singolo beep (sine wave con fade in/out anti-click).
function scheduleBeep(ctx, freq, startTime, dur) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, startTime);
  // Fade in/out per evitare click
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(0.35, startTime + 0.02);
  gain.gain.setValueAtTime(0.35, startTime + dur - 0.02);
  gain.gain.linearRampToValueAtTime(0, startTime + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + dur);
  return osc;
}
